import addToCart from '../lib/addToCart';
import updateCart from '../lib/updateCart';
import toggleAtcNotification from '../lib/toggleAtcNotification';
import getCart from '../lib/getCart';
import buildPdpCartItems from '../lib/buildPdpCartItems';
import trapFocus from '../utils/trapFocus';
import {
  appendStagedWarrantyToItems,
  findWarrantyLineItem,
  hasStagedWarrantyForProduct,
  initWarrantySelection,
  markWarrantyInCartFromCart,
  persistWarrantySelectionForProduct,
  setWarrantySelection,
} from '../lib/warrantySelection';

const SELECTOR_WARRANTY_VARIANT = '[data-warranty-variant-select]';
const MODAL_SELECTOR = '[data-warranty-atc-modal]';

let pendingAtc = null;

const setBtnLoadingState = (button, spinner, loading) => {
  if (!button) return;
  button.classList.toggle('pointer-events-none', loading);
  button.classList.toggle('opacity-50', loading);
  if (spinner) spinner.classList.toggle('hidden', !loading);
};

const getModal = () => document.querySelector(MODAL_SELECTOR);

const getConfig = () => {
  const configScript = getModal()?.querySelector('[data-warranty-drawer-config]');
  if (!configScript) return null;
  try {
    return JSON.parse(configScript.textContent);
  } catch (error) {
    console.error('Failed to parse warranty ATC modal config:', error);
    return null;
  }
};

const getSelectedVariantFromSelect = select => {
  const option = select.options[select.selectedIndex];
  if (!option) return null;
  return {
    variantId: Number(option.value),
    variantTitle: option.dataset.variantTitle || option.textContent.trim(),
    variantPriceFormatted: option.dataset.variantPriceFormatted || '',
  };
};

const updateSelectDisplay = select => {
  const wrap = select.closest('[data-warranty-select-wrap]');
  if (!wrap) return;
  const option = select.options[select.selectedIndex];
  const titleEl = wrap.querySelector('[data-warranty-select-title]');
  const priceEl = wrap.querySelector('[data-warranty-select-price]');
  if (titleEl) {
    titleEl.textContent = option?.dataset.variantTitle || '';
  }
  if (priceEl) {
    priceEl.textContent = option?.dataset.variantPriceFormatted || '';
  }
};

const getAttributes = form => {
  const attributeInputs = form.querySelectorAll('input[name^="attributes["]');
  const attributes = {};

  attributeInputs.forEach(input => {
    const match = input.name.match(/^attributes\[(.+)\]$/);
    if (match) {
      attributes[match[1]] = input.value;
    }
  });
  return attributes;
};

export const executeAddToCart = async ({
  form,
  variantId,
  quantity,
  sellingPlan,
  submitter,
  spinner,
  extraItems = [],
  markWarranty = false,
  parentProductId = null,
  warrantyVariantIds = null,
  showNotification = true,
  onCartAdded = null,
}) => {
  const baseItems = buildPdpCartItems(form, variantId, quantity, sellingPlan);
  const staged = appendStagedWarrantyToItems(baseItems, quantity);
  const items = [...staged.items, ...extraItems];
  const shouldMarkWarranty = markWarranty || staged.added;
  const warrantyParentId = parentProductId || staged.parentProductId;

  const addedProduct = await addToCart(items);

  if (typeof onCartAdded === 'function') {
    onCartAdded();
  }

  const attributes = getAttributes(form);
  if (Object.keys(attributes).length > 0) {
    await updateCart(attributes);
  }

  if (shouldMarkWarranty && warrantyParentId) {
    try {
      await markWarrantyInCartFromCart(warrantyParentId, { variantIds: warrantyVariantIds });
    } catch (error) {
      console.warn('Could not sync warranty cart state:', error);
    }
  }

  if (showNotification) {
    toggleAtcNotification(addedProduct);
  }

  document.dispatchEvent(new CustomEvent('cart:updated'));
  return addedProduct;
};

export const shouldShowWarrantyAtcModal = async () => {
  const warrantyRoot = document.querySelector('[data-product-warranty]');
  const modal = getModal();
  if (!warrantyRoot || !modal) return false;

  const config = getConfig();
  if (!config?.parentProductId) return false;

  initWarrantySelection({
    parentProductId: config.parentProductId,
    variantIds: config.variantIds,
  });

  if (hasStagedWarrantyForProduct(config.parentProductId)) {
    return false;
  }

  const cart = await getCart();
  return !findWarrantyLineItem(cart, config);
};

const clearPendingAtcLoading = () => {
  if (!pendingAtc) return;
  setBtnLoadingState(pendingAtc.submitter, pendingAtc.spinner, false);
};

const closeWarrantyAtcModal = () => {
  const modal = getModal();
  if (!modal) return;

  try {
    if (modal.open) {
      modal.close();
    }
  } catch (error) {
    console.error('Failed to close warranty ATC modal:', error);
  }

  modal.removeAttribute('open');
  clearPendingAtcLoading();
  pendingAtc = null;
};

const completePendingAtc = async ({ includeWarranty }) => {
  if (!pendingAtc) return;

  const config = getConfig();
  const { form, variantId, quantity, sellingPlan, submitter, spinner } = pendingAtc;
  let markWarranty = false;
  let parentProductId = null;

  if (includeWarranty) {
    const select = getModal()?.querySelector(SELECTOR_WARRANTY_VARIANT);
    const selected = select ? getSelectedVariantFromSelect(select) : null;
    if (!selected?.variantId || !config?.parentProductId) return;

    initWarrantySelection({
      parentProductId: config.parentProductId,
      variantIds: config.variantIds,
    });
    setWarrantySelection({
      parentProductId: config.parentProductId,
      variantId: selected.variantId,
      variantTitle: selected.variantTitle,
      variantPriceFormatted: selected.variantPriceFormatted,
      isEditing: false,
    });

    markWarranty = true;
    parentProductId = config.parentProductId;
    persistWarrantySelectionForProduct(config.parentProductId, selected);
  }

  setBtnLoadingState(submitter, spinner, true);

  let cartAdded = false;

  try {
    await executeAddToCart({
      form,
      variantId,
      quantity,
      sellingPlan,
      submitter,
      spinner,
      markWarranty,
      parentProductId,
      warrantyVariantIds: config?.variantIds,
      showNotification: true,
      onCartAdded: () => {
        cartAdded = true;
        closeWarrantyAtcModal();
      },
    });
    pendingAtc = null;
  } catch (error) {
    console.error('Error completing add to cart from warranty modal:', error);
  } finally {
    setBtnLoadingState(submitter, spinner, false);
    if (cartAdded) {
      closeWarrantyAtcModal();
    }
  }
};

const dismissWarrantyAtcModal = () => {
  if (pendingAtc) {
    completePendingAtc({ includeWarranty: false });
    return;
  }
  closeWarrantyAtcModal();
};

const openWarrantyAtcModal = pending => {
  const modal = getModal();
  if (!modal) return;

  pendingAtc = pending;

  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  modal.showModal();
  trapFocus(modal);

  modal.querySelectorAll(SELECTOR_WARRANTY_VARIANT).forEach(select => {
    updateSelectDisplay(select);
  });
};

const initProductWarrantyAtcModal = () => {
  const modal = getModal();
  if (!modal || modal.dataset.warrantyAtcModalInit === 'true') return;

  modal.dataset.warrantyAtcModalInit = 'true';

  modal.querySelectorAll(SELECTOR_WARRANTY_VARIANT).forEach(select => {
    select.addEventListener('change', () => updateSelectDisplay(select));
    updateSelectDisplay(select);
  });

  modal.querySelector('[data-warranty-atc-continue]')?.addEventListener('click', () => {
    completePendingAtc({ includeWarranty: false });
  });

  modal.querySelector('[data-warranty-atc-add-cover]')?.addEventListener('click', () => {
    completePendingAtc({ includeWarranty: true });
  });

  modal.querySelectorAll('[data-close-warranty-atc-modal]').forEach(button => {
    button.addEventListener('click', dismissWarrantyAtcModal);
  });

  modal.addEventListener('cancel', event => {
    event.preventDefault();
  });
};

export { openWarrantyAtcModal };

export default initProductWarrantyAtcModal;
